import * as RadixDialog from "@radix-ui/react-dialog";
import { Loader2, Search } from "lucide-react";
import * as React from "react";
import { cn } from "../../../shared/components/ui/cn";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "../../../shared/components/ui/command";
import { DialogDescription, DialogTitle } from "../../../shared/components/ui/dialog";
import type { SearchResult } from "../hooks/useSearch";
import { SearchResultItem } from "./SearchResultItem";

/**
 * Custom Dialog components for search - anchored from top instead of centered
 */
const Dialog = RadixDialog.Root;

const SearchDialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ className, children, ...props }, ref) => (
  <RadixDialog.Portal>
    {/* Overlay: Full-screen backdrop with blur effect */}
    <RadixDialog.Overlay
      className={cn(
        "fixed inset-0 z-50",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "duration-500",
      )}
    />
    {/* Wrapper: Anchored from top at 25vh instead of centered */}
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[25vh]">
      {/* Content: The actual dialog box */}
      <RadixDialog.Content
        ref={ref}
        className={cn(
          "grid w-full gap-4 rounded-lg border text-white",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:slide-out-to-top-8 data-[state=open]:slide-in-from-top-8",
          "duration-500",
          className,
        )}
        {...props}
      >
        {children}
      </RadixDialog.Content>
    </div>
  </RadixDialog.Portal>
));

SearchDialogContent.displayName = "SearchDialogContent";

/**
 * Custom CommandInput with inline autocomplete support
 * Shows ghosted suggestion text that can be accepted with Right Arrow
 */
const CommandInputWithAutocomplete = React.forwardRef<
  HTMLInputElement,
  {
    value: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    ghostText?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  }
>(({ value, onValueChange, placeholder, className, ghostText, onKeyDown }, ref) => {
  return (
    <div className="flex items-center px-4 bg-transparent h-14 relative" {...{ "cmdk-input-wrapper": "" }}>
      <Search className="mr-3 h-5 w-5 shrink-0 text-neutral-500 z-10" />

      {/* Ghost text overlay */}
      {ghostText && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pl-8 pointer-events-none text-base text-neutral-600">
          <span className="invisible">{value}</span>
          <span>{ghostText}</span>
        </div>
      )}

      {/* Actual input */}
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={cn(
          "flex h-full w-full bg-transparent py-0 text-base outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50 text-white relative z-10 focus:shadow-none",
          className,
        )}
      />
    </div>
  );
});

CommandInputWithAutocomplete.displayName = "CommandInputWithAutocomplete";

interface DesktopSearchDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly inputValue: string;
  readonly onInputChange: (value: string) => void;
  readonly loading: boolean;
  readonly results: SearchResult[];
  readonly recentSearches: string[];
  readonly onResultSelect: (result: SearchResult) => void;
  readonly onRecentSearchSelect: (query: string) => void;
  readonly onViewAllResults: () => void;
  readonly autocompleteSuggestion?: string;
  readonly onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Desktop search command dialog - keyboard-friendly palette interface
 */
export function DesktopSearchDialog({
  open,
  onOpenChange,
  inputValue,
  onInputChange,
  loading,
  results,
  recentSearches,
  onResultSelect,
  onRecentSearchSelect,
  onViewAllResults,
  autocompleteSuggestion,
  onKeyDown,
}: DesktopSearchDialogProps) {
  // Calculate the ghost text to show
  const ghostText =
    autocompleteSuggestion && inputValue.length > 0 ? autocompleteSuggestion.slice(inputValue.length) : "";
  return (
    // Dialog: Radix UI dialog root component
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* SearchDialogContent: Custom dialog content anchored from top at 25vh */}
      <SearchDialogContent className="rounded-2xl bg-neutral-950/60 backdrop-blur-lg border border-lumi-accent pb-0 pt-3 px-4 gap-0 max-w-4xl w-[90vw]">
        {/* Hidden accessibility labels */}
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search for movies and TV shows</DialogDescription>

        {/* Command: cmdk command palette wrapper */}
        <Command className="bg-black-20 border-0" shouldFilter={false}>
          {/* Custom input with inline autocomplete */}
          <div className={inputValue ? "border-b border-neutral-800" : ""}>
            <CommandInputWithAutocomplete
              value={inputValue}
              onValueChange={onInputChange}
              placeholder="Search by movie name, genre, country..."
              ghostText={ghostText}
              onKeyDown={onKeyDown}
            />
          </div>
          {/* CommandList: Scrollable container for results with hidden scrollbar */}
          <CommandList className="text-sm text-neutral-500 max-h-[450px] scrollbar-hide">
            {/* Always render CommandEmpty but hide it when not needed */}
            <CommandEmpty className={inputValue && !loading ? "text-center py-12 pt" : "hidden"}>
              No results found for "{inputValue}"
            </CommandEmpty>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-lumi-accent" />
              </div>
            )}

            {!loading && !inputValue && recentSearches.length > 0 && (
              <CommandGroup heading="Recent Searches">
                {recentSearches.map((query) => (
                  <CommandItem key={query} onSelect={() => onRecentSearchSelect(query)} className="cursor-pointer">
                    <span className="text-neutral-50">{query}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!loading && results.length > 0 && (
              <>
                <CommandGroup heading="Search Results">
                  {results.map((result, index) => (
                    <SearchResultItem
                      key={result.id}
                      result={result}
                      index={index}
                      onSelect={() => onResultSelect(result)}
                    />
                  ))}
                </CommandGroup>
                <div className="mt-2">
                  <CommandItem
                    onSelect={onViewAllResults}
                    className="cursor-pointer text-neutral-600 hover:text-lumi-accent hover:bg-lumi-accent/10 data-[selected=true]:bg-lumi-accent/10 data-[selected=true]:text-lumi-accent justify-center font-medium text-sm mx-2 my-2"
                  >
                    View All Results →
                  </CommandItem>
                </div>
              </>
            )}
          </CommandList>
        </Command>
      </SearchDialogContent>
    </Dialog>
  );
}
